import { z } from 'zod';
import { normalizedUuidSchema, paginationMetaSchema, paginationSchema } from './common';
import { DESIGN_ASSET_TEMP_PREFIX } from './design-asset';
import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  IMAGE_CONTENT_TYPE_EXTENSIONS,
} from './image-asset';

/** 정책: docs/products/review-policy.md */
export const REVIEW_RATING_MIN = 1;
export const REVIEW_RATING_MAX = 5;
export const REVIEW_CONTENT_MIN = 10;
export const REVIEW_CONTENT_MAX = 1000;
export const REVIEW_IMAGES_MAX = 5;
export const REVIEW_WRITE_WINDOW_DAYS = 90;
export const REVIEW_PAGE_SIZE = 10;
export const REVIEW_IMAGE_PRESIGNS_PER_HOUR = 30;
export const REVIEW_HIDE_REASON_MAX = 500;
export const REVIEW_STATES_MAX_ORDERS = 50;
export const REVIEW_IMAGE_PREFIX = 'reviews/';

export const reviewImageFileKeyFor = (input: {
  readonly imageId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `${REVIEW_IMAGE_PREFIX}${input.imageId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;

/** 고객 업로드 임시 경로는 디자인 자산과 같은 tmp/uploads/ 를 쓴다(버킷 수명주기 1일, ADR-0008). */
export const reviewImageTemporaryKeyFor = (input: {
  readonly imageId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `${DESIGN_ASSET_TEMP_PREFIX}${input.imageId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;

export const REVIEW_ERROR_CODES = [
  'REVIEW_NOT_FOUND',
  'ORDER_NOT_ELIGIBLE',
  'NOT_DELIVERED',
  'DEADLINE_PASSED',
  'ALREADY_WRITTEN',
  'HIDDEN_REVIEW',
  'INVALID_IMAGE',
  'CONFLICT',
] as const;
export type ReviewErrorCode = (typeof REVIEW_ERROR_CODES)[number];

const timestampSchema = z.iso.datetime();
const calendarDateSchema = z.iso.date();

export const reviewRatingSchema = z.number().int().min(REVIEW_RATING_MIN).max(REVIEW_RATING_MAX);
/** DB CHECK(char_length)와 같은 코드 포인트 단위로 센다 — 이모지 같은 서로게이트 쌍도 한 글자다. */
export const reviewContentSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      const length = Array.from(value).length;
      return length >= REVIEW_CONTENT_MIN && length <= REVIEW_CONTENT_MAX;
    },
    { message: `후기는 ${REVIEW_CONTENT_MIN}자 이상 ${REVIEW_CONTENT_MAX}자 이하로 써 주세요.` },
  );
export const reviewImageIdsSchema = z
  .array(normalizedUuidSchema)
  .max(REVIEW_IMAGES_MAX)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: '같은 사진을 두 번 넣을 수 없습니다.',
  });

export const reviewSortSchema = z.enum(['LATEST', 'RATING_DESC', 'RATING_ASC']);
export type ReviewSort = z.infer<typeof reviewSortSchema>;

export const reviewCursorSchema = z.string().min(1).max(200).nullable().default(null);

export const reviewImageViewSchema = z.strictObject({
  id: normalizedUuidSchema,
  url: z.string(),
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
});
export type ReviewImageView = z.infer<typeof reviewImageViewSchema>;

export const productReviewSummarySchema = z.strictObject({
  averageRating: z.number().nullable(),
  count: z.number().int().nonnegative(),
});
export type ProductReviewSummary = z.infer<typeof productReviewSummarySchema>;

export const publicReviewSchema = z.strictObject({
  id: normalizedUuidSchema,
  rating: reviewRatingSchema,
  content: z.string(),
  images: z.array(reviewImageViewSchema),
  quantity: z.number().int().positive(),
  colorName: z.string(),
  printMethodName: z.string().nullable(),
  authorName: z.string(),
  createdAt: timestampSchema,
  isEdited: z.boolean(),
});
export type PublicReview = z.infer<typeof publicReviewSchema>;

export const publicReviewPageSchema = z.strictObject({
  items: z.array(publicReviewSchema),
  nextCursor: z.string().nullable(),
});
export type PublicReviewPage = z.infer<typeof publicReviewPageSchema>;

/** HOME 고객 후기 섹션에 올리는 후기 수 — 데스크톱 한 줄(4장). */
export const HOME_REVIEW_HIGHLIGHTS_MAX = 4;

/** HOME 고객 후기 — 공개 후기에 어느 상품의 후기인지(공개 상품만)를 덧붙인다. */
export const homeReviewHighlightSchema = publicReviewSchema.extend({
  product: z.strictObject({ id: normalizedUuidSchema, name: z.string().min(1) }),
});
export type HomeReviewHighlight = z.infer<typeof homeReviewHighlightSchema>;

/** 별점 높은순 → 최신순, 상품이 겹치지 않게 먼저 뽑고 모자라면 채운다. 배열 순서가 노출 순서다. */
export const homeReviewHighlightListSchema = z
  .array(homeReviewHighlightSchema)
  .max(HOME_REVIEW_HIGHLIGHTS_MAX);
export type HomeReviewHighlightList = z.infer<typeof homeReviewHighlightListSchema>;

export const productReviewSummaryInputSchema = z.strictObject({ productId: normalizedUuidSchema });

export const listProductReviewsInputSchema = z.strictObject({
  productId: normalizedUuidSchema,
  sort: reviewSortSchema.default('LATEST'),
  photoOnly: z.boolean().default(false),
  cursor: reviewCursorSchema,
});
export type ListProductReviewsInput = z.infer<typeof listProductReviewsInputSchema>;

export const reviewOrderStateSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    orderId: normalizedUuidSchema,
    kind: z.literal('WRITABLE'),
    deadline: calendarDateSchema,
  }),
  z.strictObject({
    orderId: normalizedUuidSchema,
    kind: z.literal('WRITTEN'),
    reviewId: normalizedUuidSchema,
  }),
  z.strictObject({ orderId: normalizedUuidSchema, kind: z.literal('EXPIRED') }),
  z.strictObject({ orderId: normalizedUuidSchema, kind: z.literal('NOT_ELIGIBLE') }),
]);
export type ReviewOrderState = z.infer<typeof reviewOrderStateSchema>;

export const reviewStatesForOrdersInputSchema = z.strictObject({
  orderIds: z.array(normalizedUuidSchema).min(1).max(REVIEW_STATES_MAX_ORDERS),
});
export const reviewStatesForOrdersSchema = z.array(reviewOrderStateSchema);

export const reviewStatusSchema = z.enum(['VISIBLE', 'HIDDEN']);

export const myReviewSchema = publicReviewSchema.omit({ authorName: true }).extend({
  status: reviewStatusSchema,
  orderId: normalizedUuidSchema,
  productId: normalizedUuidSchema,
  productName: z.string(),
  updatedAt: timestampSchema,
});
export type MyReview = z.infer<typeof myReviewSchema>;

export const myReviewPageSchema = z.strictObject({
  items: z.array(myReviewSchema),
  nextCursor: z.string().nullable(),
});
export type MyReviewPage = z.infer<typeof myReviewPageSchema>;

export const myReviewListInputSchema = z.strictObject({ cursor: reviewCursorSchema });
export const reviewIdInputSchema = z.strictObject({ reviewId: normalizedUuidSchema });

export const createReviewInputSchema = z.strictObject({
  orderId: normalizedUuidSchema,
  rating: reviewRatingSchema,
  content: reviewContentSchema,
  imageIds: reviewImageIdsSchema,
});
export type CreateReviewInput = z.infer<typeof createReviewInputSchema>;

export const updateReviewInputSchema = z.strictObject({
  reviewId: normalizedUuidSchema,
  rating: reviewRatingSchema,
  content: reviewContentSchema,
  imageIds: reviewImageIdsSchema,
  expectedUpdatedAt: timestampSchema,
});
export type UpdateReviewInput = z.infer<typeof updateReviewInputSchema>;

export const reviewMutationResultSchema = z.strictObject({
  id: normalizedUuidSchema,
  updatedAt: timestampSchema,
});
export type ReviewMutationResult = z.infer<typeof reviewMutationResultSchema>;

export const presignReviewImageInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export const presignReviewImageResultSchema = z.strictObject({
  imageId: normalizedUuidSchema,
  uploadUrl: z.string(),
  expiresInSeconds: z.number().int().positive(),
});
export type PresignReviewImageResult = z.infer<typeof presignReviewImageResultSchema>;
export const confirmReviewImageInputSchema = z.strictObject({ imageId: normalizedUuidSchema });

/** 관리자 목록 — 쪽 번호 방식(다른 관리자 목록과 같다). 고객 화면의 더 보기(커서)와 다르다. */
export const adminReviewListInputSchema = z.strictObject({
  ...paginationSchema.shape,
  productId: normalizedUuidSchema.optional(),
  rating: reviewRatingSchema.optional(),
  status: reviewStatusSchema.optional(),
  hasPhoto: z.boolean().optional(),
});
export type AdminReviewListInput = z.infer<typeof adminReviewListInputSchema>;

export const adminReviewRowSchema = z.strictObject({
  id: normalizedUuidSchema,
  productId: normalizedUuidSchema,
  productName: z.string(),
  rating: reviewRatingSchema,
  contentPreview: z.string(),
  imageCount: z.number().int().nonnegative(),
  authorName: z.string(),
  status: reviewStatusSchema,
  createdAt: timestampSchema,
});
export type AdminReviewRow = z.infer<typeof adminReviewRowSchema>;
export const adminReviewPageSchema = z.strictObject({
  items: z.array(adminReviewRowSchema),
  meta: paginationMetaSchema,
});
export type AdminReviewPage = z.infer<typeof adminReviewPageSchema>;

export const adminReviewDetailSchema = z.strictObject({
  id: normalizedUuidSchema,
  productId: normalizedUuidSchema,
  productName: z.string(),
  rating: reviewRatingSchema,
  content: z.string(),
  images: z.array(reviewImageViewSchema),
  quantity: z.number().int().positive(),
  colorName: z.string(),
  printMethodName: z.string().nullable(),
  author: z.strictObject({ name: z.string(), email: z.string(), withdrawn: z.boolean() }),
  orderId: normalizedUuidSchema,
  orderNumber: z.string(),
  status: reviewStatusSchema,
  hidden: z
    .strictObject({ at: timestampSchema, byName: z.string(), reason: z.string() })
    .nullable(),
  editedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type AdminReviewDetail = z.infer<typeof adminReviewDetailSchema>;

export const hideReviewInputSchema = z.strictObject({
  reviewId: normalizedUuidSchema,
  reason: z.string().trim().min(1).max(REVIEW_HIDE_REASON_MAX),
  expectedUpdatedAt: timestampSchema,
});
export type HideReviewInput = z.infer<typeof hideReviewInputSchema>;
export const unhideReviewInputSchema = z.strictObject({
  reviewId: normalizedUuidSchema,
  expectedUpdatedAt: timestampSchema,
});
export type UnhideReviewInput = z.infer<typeof unhideReviewInputSchema>;
